import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import { JUDGE_MODEL_KEYS, type EvalRun } from "~/services/evals";
import { shortModelName } from "./primitives/format";
import { Fact, Spec, SpecRow } from "./primitives/layout";

interface RunConfigCardProps {
  run: EvalRun;
  /** Distinct cases that posted a result, for checking the runner's own count. */
  observedCaseCount: number;
  /** Result rows the API returned, for checking the runner's own count. */
  observedResultCount: number;
}

/**
 * How the run was configured and what it judged with. Without these a run's
 * numbers cannot be read: three iterations of nine cases is a different
 * measurement from one iteration of twenty-seven.
 *
 * The judge models come from the run's own `judge_config`, which the runner
 * rewrites on completion with the *effective* configuration — so this is what the
 * run was judged by, and it stays correct after the service defaults move.
 */
export const RunConfigCard = ({
  run,
  observedCaseCount,
  observedResultCount,
}: RunConfigCardProps) => {
  const params = run.run_parameters;
  const report = run.report;
  const judgeModels = JUDGE_MODEL_KEYS.map((entry) => ({
    ...entry,
    model: run.judge_config?.[entry.key] ?? null,
  })).filter((entry) => entry.model != null);
  // The runner's own counts, shown only where they disagree with what the
  // results API returned. Agreeing, they restate the summary tiles above;
  // disagreeing, they are the only sign that the run selected cases it never
  // posted a result for.
  const casesUnposted = report?.num_cases != null && report.num_cases !== observedCaseCount;
  const resultsUnposted =
    report?.results_posted != null && report.results_posted !== observedResultCount;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-semibold">Launch parameters and rubric</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-5">
        <div className="grid grid-cols-2 gap-x-8 gap-y-4 md:grid-cols-4">
          <Fact label="Iterations">
            <Value>
              {params.iterations != null ? `${params.iterations} per case` : "Not recorded"}
            </Value>
          </Fact>
          <Fact label="Concurrency">
            <Value>
              {params.max_concurrency != null ? `${params.max_concurrency} cases` : "Not recorded"}
            </Value>
          </Fact>
          <Fact label="Query rewriting">
            <Value>{params.rewrite_queries ? "On" : "Off"}</Value>
          </Fact>
          <Fact label="Conversations kept">
            {/* Not a detail: this is what decides whether any of the run's cases
                has a transcript to open, so a reviewer finding every Transcript
                cell empty can see why here. */}
            <Value>{params.keep_conversations ? "Yes" : "No — discarded after judging"}</Value>
          </Fact>
        </div>

        {(casesUnposted || resultsUnposted) && (
          <Spec>
            {casesUnposted && (
              <SpecRow label="Cases selected">
                <Value>
                  {report?.num_cases}, of which {observedCaseCount} posted a result
                </Value>
              </SpecRow>
            )}
            {resultsUnposted && (
              <SpecRow label="Results posted">
                <Value>
                  {report?.results_posted} by the runner, {observedResultCount} readable here
                </Value>
              </SpecRow>
            )}
          </Spec>
        )}

        {(judgeModels.length > 0 || run.case_created_by) && (
          <div>
            <h3 className="text-xs font-medium text-muted-foreground">Judged by</h3>
            <Spec className="mt-2 max-w-xl">
              {judgeModels.map((entry) => (
                <SpecRow key={entry.key} label={entry.label}>
                  {/* Truncated the way the runs table truncates a model, so one
                      model reads the same everywhere. Full id on hover. */}
                  <span className="font-mono text-xs" title={entry.model ?? undefined}>
                    {shortModelName(entry.model ?? "")}
                  </span>
                </SpecRow>
              ))}
              {run.case_created_by && (
                // A second case selector alongside tags, and easy to forget it
                // was set — a run scoped to one author looks like a run that
                // lost cases.
                <SpecRow label="Cases authored by">
                  <Value className="truncate">{run.case_created_by}</Value>
                </SpecRow>
              )}
            </Spec>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const Value = ({ children, className }: { children: React.ReactNode; className?: string }) => (
  <span className={`block text-sm tabular-nums text-foreground ${className ?? ""}`}>
    {children}
  </span>
);
